import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { FaAngleRight, FaBuilding, FaCalendarCheck, FaLocationArrow, FaMapMarkerAlt, FaPlane } from 'react-icons/fa';
import { Modal, Badge, Button } from 'components/common';

const MediaContent = ({ icon: Icon, heading, content, children }) => (
  <div className="tw-flex tw-gap-3 tw-mt-3">
    <span className="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-rounded-xl tw-bg-blue-50 tw-text-blue-600 tw-flex tw-items-center tw-justify-center">{Icon ? <Icon /> : null}</span>
    <div className="tw-flex-1 tw-text-sm">
      <h6 className="tw-mb-1 tw-font-semibold tw-text-slate-700">{heading}</h6>
      {children || <p className="tw-mb-0 tw-text-slate-600">{content}</p>}
    </div>
  </div>
);
MediaContent.propTypes = { icon: PropTypes.elementType, heading: PropTypes.string, content: PropTypes.string, children: PropTypes.node };

const CalendarEventModal = ({ setIsOpenEventModal, isOpenEventModal, modalEventContent }) => {
  const handleClose = () => setIsOpenEventModal(false);

  if (!isOpenEventModal || !modalEventContent.event) return null;

  const { id, title, end, start } = modalEventContent.event;
  const {
    booking_number, destination, country_name, site_name, type_of_travel, purpose_of_travel,
    description_purpose_of_travel, traveler_names, job_number, project, status_name, submited_by, submited_date,
  } = modalEventContent.event.extendedProps;

  return (
    <Modal
      show={isOpenEventModal}
      onHide={handleClose}
      title={(
        <div>
          <h5 className="tw-mb-0 tw-font-semibold tw-text-slate-800">{title}</h5>
          <p className="tw-mb-0 tw-text-xs tw-mt-1 tw-italic tw-text-slate-500">Submited by <span className="tw-text-blue-600">{submited_by} | {submited_date}</span></p>
        </div>
      )}
      footer={(
        <Link to={`/booking/travel/${id}`} target="_blank">
          <Button size="sm" variant="primary" rightIcon={FaAngleRight}>See more details</Button>
        </Link>
      )}
    >
      <div className="tw-text-sm">
        <div className="tw-flex tw-justify-between tw-mb-4">
          <p className="tw-mb-0">Booking ID#: <strong>{booking_number}</strong></p>
          <p className="tw-mb-0">Status: <Badge variant="success" size="sm">{status_name}</Badge></p>
        </div>

        <MediaContent icon={FaBuilding} heading="From site office" content={site_name} />

        <MediaContent icon={FaMapMarkerAlt} heading="Destination">
          <div className="tw-mb-2">Destination: <span className="tw-text-blue-600">{destination}</span></div>
          <div className="tw-mb-2">Type of travel: <Badge variant="primary" size="sm">{type_of_travel}</Badge></div>
          <div className="tw-mb-2">Trip to country: <span className="tw-text-blue-600">{country_name}</span></div>
        </MediaContent>

        <MediaContent icon={FaCalendarCheck} heading="Departure and Return Date Time">
          <div className="tw-mb-2">Begin date: <span className="tw-text-blue-600">{dayjs(start).format('dddd, MMMM DD, YYYY, hh:mm A')}</span></div>
          <div className="tw-mb-2">End date: <span className="tw-text-blue-600">{dayjs(end).format('dddd, MMMM DD, YYYY, hh:mm A')}</span></div>
        </MediaContent>

        <MediaContent icon={FaLocationArrow} heading="Purpose of trip">
          <div className="tw-text-red-600">{purpose_of_travel}</div>
        </MediaContent>

        <MediaContent heading="Description">
          <div style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: description_purpose_of_travel }} />
        </MediaContent>

        {job_number && (
          <MediaContent heading="Customer & Project">
            <div className="tw-mb-2">Customers: <span className="tw-text-blue-600">{project?.customer_name}</span></div>
            <div className="tw-mb-2">Project: <span className="tw-text-blue-600">{project?.project_name}</span></div>
            <div className="tw-mb-2">Job number: <span className="tw-text-blue-600">{job_number}</span></div>
          </MediaContent>
        )}

        <MediaContent icon={FaPlane} heading="Travelers">
          <span className="tw-flex tw-flex-wrap tw-gap-1">
            {(traveler_names || []).map((name, index) => <Badge key={index} variant="info" size="sm">{name}</Badge>)}
          </span>
        </MediaContent>
      </div>
    </Modal>
  );
};

CalendarEventModal.propTypes = {
  setIsOpenEventModal: PropTypes.func.isRequired,
  isOpenEventModal: PropTypes.bool.isRequired,
  modalEventContent: PropTypes.object.isRequired,
};

export default CalendarEventModal;
